import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { memberService, postService, photoService } from '../../services/api';
import Post from '../../components/Post/Post';
import {
  FiEdit2, FiMapPin, FiBriefcase, FiCalendar, FiMail,
  FiPhone, FiSave, FiX, FiCamera
} from 'react-icons/fi';
import { format } from 'date-fns';
import ProfilePhotos from '../../components/ProfilePhotos/ProfilePhotos';
import Relationships from '../../components/Relationships/Relationships';
import './Profile.css';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('posts');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    maidenName: '',
    nickname: '',
    bio: '',
    currentCity: '',
    hometown: '',
    occupation: '',
    phoneNumber: '',
    dateOfBirth: '',
    gender: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchPosts();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await memberService.getCurrentMember();
      if (response.success) {
        setProfile(response.data);
        setFormData({
          firstName: response.data.firstName || '',
          lastName: response.data.lastName || '',
          middleName: response.data.middleName || '',
          maidenName: response.data.maidenName || '',
          nickname: response.data.nickname || '',
          bio: response.data.bio || '',
          currentCity: response.data.currentCity || '',
          hometown: response.data.hometown || '',
          occupation: response.data.occupation || '',
          phoneNumber: response.data.phoneNumber || '',
          dateOfBirth: response.data.dateOfBirth ? response.data.dateOfBirth.split('T')[0] : '',
          gender: response.data.gender || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const fetchPosts = async () => {
    if (!user?.memberID) return;

    try {
      setLoadingPosts(true);
      const response = await postService.getMemberPosts(user.memberID);
      if (response.success) {
        const postData = Array.isArray(response.data)
          ? response.data
          : (response.data.posts || response.data.items || []);
        setPosts(postData);
      }
    } catch (err) {
      console.error('Failed to fetch posts:', err);
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await memberService.updateProfile(formData);
      if (response.success) {
        setProfile(response.data);
        updateUser(response.data);
        setEditing(false);
      } else {
        setError(response.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      middleName: profile.middleName || '',
      maidenName: profile.maidenName || '',
      nickname: profile.nickname || '',
      bio: profile.bio || '',
      currentCity: profile.currentCity || '',
      hometown: profile.hometown || '',
      occupation: profile.occupation || '',
      phoneNumber: profile.phoneNumber || '',
      dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.split('T')[0] : '',
      gender: profile.gender || ''
    });
    setEditing(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    try {
      setUploadingAvatar(true);
      const fd = new FormData();
      fd.append('file', file);
      const uploadResponse = await photoService.uploadPhoto(fd);
      if (uploadResponse.success) {
        const response = await memberService.updateProfilePhoto(uploadResponse.data.fileURL);
        if (response.success) {
          await fetchProfile();
          updateUser({ ...user, profilePhotoURL: uploadResponse.data.fileURL });
        }
      }
    } catch (err) {
      console.error('Failed to upload profile photo:', err);
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    try {
      setUploadingCover(true);
      const fd = new FormData();
      fd.append('file', file);
      const uploadResponse = await photoService.uploadPhoto(fd);
      if (uploadResponse.success) {
        const response = await memberService.updateCoverPhoto(uploadResponse.data.fileURL);
        if (response.success) {
          await fetchProfile();
        }
      }
    } catch (err) {
      console.error('Failed to upload cover photo:', err);
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const handlePostDeleted = async (postId) => {
    try {
      const response = await postService.deletePost(postId);
      if (response.success) {
        setPosts(prev => prev.filter(p => p.postID !== postId));
      }
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="loading-state">
            <span className="spinner spinner-lg"></span>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="error-state card">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchProfile}>Try Again</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Cover Photo */}
      <div className="profile-cover">
        {profile?.coverPhotoURL ? (
          <img src={profile.coverPhotoURL} alt="Cover" className="cover-image" />
        ) : (
          <div className="cover-placeholder"></div>
        )}
        <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverUpload} hidden />
        <button className="cover-edit-btn btn btn-ghost" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover}>
          <FiCamera /> {uploadingCover ? 'Uploading...' : 'Edit Cover'}
        </button>
      </div>

      <div className="container">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-avatar-section">
            <div className="profile-avatar">
              {profile?.profilePhotoURL ? (
                <img src={profile.profilePhotoURL} alt={profile.fullName} />
              ) : (
                <div className="avatar-placeholder">
                  {getInitials(profile?.firstName, profile?.lastName)}
                </div>
              )}
              <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} hidden />
              <button className="avatar-edit-btn" onClick={() => avatarInputRef.current?.click()} disabled={uploadingAvatar} title="Change profile photo">
                <FiCamera />
              </button>
            </div>
          </div>

          <div className="profile-info">
            <h1 className="profile-name">
              {profile?.fullName}
              {profile?.nickname && <span className="profile-nickname">"{profile.nickname}"</span>}
            </h1>

            {profile?.bio && !editing && (
              <p className="profile-bio">{profile.bio}</p>
            )}

            <div className="profile-details">
              {profile?.currentCity && (
                <span className="profile-detail">
                  <FiMapPin /> {profile.currentCity}
                </span>
              )}
              {profile?.occupation && (
                <span className="profile-detail">
                  <FiBriefcase /> {profile.occupation}
                </span>
              )}
              {profile?.dateOfBirth && (
                <span className="profile-detail">
                  <FiCalendar /> {format(new Date(profile.dateOfBirth.split('T')[0] + 'T12:00:00'), 'MMMM d')}
                </span>
              )}
            </div>
          </div>

          <div className="profile-actions">
            {!editing ? (
              <button className="btn btn-primary" onClick={() => setEditing(true)}>
                <FiEdit2 /> Edit Profile
              </button>
            ) : (
              <div className="edit-actions">
                <button className="btn btn-ghost" onClick={handleCancel} disabled={saving}>
                  <FiX /> Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <span className="spinner spinner-sm"></span> Saving...
                    </>
                  ) : (
                    <>
                      <FiSave /> Save
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Edit Form */}
        {editing && (
          <div className="profile-edit-form card">
            <div className="card-body">
              <h3>Edit Profile</h3>

              {error && <div className="form-error-message">{error}</div>}

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    className="form-input"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    className="form-input"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Middle Name</label>
                  <input
                    type="text"
                    name="middleName"
                    className="form-input"
                    value={formData.middleName}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Maiden Name</label>
                  <input
                    type="text"
                    name="maidenName"
                    className="form-input"
                    value={formData.maidenName}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Nickname</label>
                  <input
                    type="text"
                    name="nickname"
                    className="form-input"
                    value={formData.nickname}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Gender</label>
                  <select
                    name="gender"
                    className="form-input"
                    value={formData.gender}
                    onChange={handleChange}
                  >
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Date of Birth</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    className="form-input"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    className="form-input"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Current City</label>
                  <input
                    type="text"
                    name="currentCity"
                    className="form-input"
                    placeholder="Where do you live now?"
                    value={formData.currentCity}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Hometown</label>
                  <input
                    type="text"
                    name="hometown"
                    className="form-input"
                    placeholder="Where did you grow up?"
                    value={formData.hometown}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Occupation</label>
                  <input
                    type="text"
                    name="occupation"
                    className="form-input"
                    placeholder="What do you do?"
                    value={formData.occupation}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group form-group-full">
                  <label className="form-label">Bio</label>
                  <textarea
                    name="bio"
                    className="form-input"
                    rows={3}
                    placeholder="Tell your family a little about yourself..."
                    value={formData.bio}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        {!editing && (
          <>
            <div className="profile-tabs">
              <button
                className={`profile-tab ${activeTab === 'posts' ? 'active' : ''}`}
                onClick={() => setActiveTab('posts')}
              >
                Posts
              </button>
              <button
                className={`profile-tab ${activeTab === 'about' ? 'active' : ''}`}
                onClick={() => setActiveTab('about')}
              >
                About
              </button>
              <button
                className={`profile-tab ${activeTab === 'photos' ? 'active' : ''}`}
                onClick={() => setActiveTab('photos')}
              >
                Photos
              </button>
              <button
                className={`profile-tab ${activeTab === 'relationships' ? 'active' : ''}`}
                onClick={() => setActiveTab('relationships')}
              >
                Relationships
              </button>
            </div>

            {/* Tab Content */}
            <div className="profile-content">
              {activeTab === 'posts' && (
                <div className="profile-posts">
                  {loadingPosts ? (
                    <div className="loading-state">
                      <span className="spinner"></span>
                      <p>Loading posts...</p>
                    </div>
                  ) : posts.length === 0 ? (
                    <div className="empty-state card">
                      <div className="empty-state-icon">📝</div>
                      <h3 className="empty-state-title">No posts yet</h3>
                      <p>Share something with your family!</p>
                    </div>
                  ) : (
                    posts.map(post => (
                      <Post
                        key={post.postID}
                        post={post}
                        onDelete={handlePostDeleted}
                      />
                    ))
                  )}
                </div>
              )}

              {activeTab === 'about' && (
                <div className="profile-about card">
                  <div className="card-body">
                    <h3>About {profile?.firstName}</h3>

                    <div className="about-section">
                      {profile?.bio && (
                        <div className="about-item">
                          <h4>Bio</h4>
                          <p>{profile.bio}</p>
                        </div>
                      )}

                      <div className="about-grid">
                        {profile?.email && (
                          <div className="about-item">
                            <FiMail className="about-icon" />
                            <div>
                              <h4>Email</h4>
                              <p>{profile.email}</p>
                            </div>
                          </div>
                        )}

                        {profile?.phoneNumber && (
                          <div className="about-item">
                            <FiPhone className="about-icon" />
                            <div>
                              <h4>Phone</h4>
                              <p>{profile.phoneNumber}</p>
                            </div>
                          </div>
                        )}

                        {profile?.currentCity && (
                          <div className="about-item">
                            <FiMapPin className="about-icon" />
                            <div>
                              <h4>Current City</h4>
                              <p>{profile.currentCity}</p>
                            </div>
                          </div>
                        )}

                        {profile?.hometown && (
                          <div className="about-item">
                            <FiMapPin className="about-icon" />
                            <div>
                              <h4>Hometown</h4>
                              <p>{profile.hometown}</p>
                            </div>
                          </div>
                        )}

                        {profile?.occupation && (
                          <div className="about-item">
                            <FiBriefcase className="about-icon" />
                            <div>
                              <h4>Occupation</h4>
                              <p>{profile.occupation}</p>
                            </div>
                          </div>
                        )}

                        {profile?.dateOfBirth && (
                          <div className="about-item">
                            <FiCalendar className="about-icon" />
                            <div>
                              <h4>Birthday</h4>
                              <p>{format(new Date(profile.dateOfBirth.split('T')[0] + 'T12:00:00'), 'MMMM d')}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'photos' && (
                <div className="profile-photos card">
                  <div className="card-body">
                    <ProfilePhotos memberId={user?.memberID} isOwnProfile={true} />
                  </div>
                </div>
              )}

              {activeTab === 'relationships' && (
                <div className="profile-relationships card">
                  <div className="card-body">
                    <Relationships memberId={user?.memberID} isOwnProfile={true} />
                  </div>
                </div>
              )}

            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;
